<script lang="ts">
  import { api } from '$shared/api';
  import type { Duel } from '$types/index';
  import LoadingSkeleton from '$shared/LoadingSkeleton.svelte';
  import DuelCard from '$components/shared/DuelCard.svelte';
  import HomeFeed from '$components/duels/HomeFeed.svelte';
  import { dismissDuel, filterDismissed } from '$lib/utils/dismissed-cards';
  import { modeLabel } from '$lib/utils/duel-phase';

  const VOTE_MODES = ['classic', 'league', 'rapid', 'bracket'] as const;

  let duels = $state<Duel[]>([]);
  let loading = $state(true);
  let error = $state('');

  // Filter state
  let filterGenre = $state('');
  let filterMode = $state('');
  let filterStatus = $state('');

  // Mobile swipe-hint visibility — once-per-browser localStorage flag.
  const HINT_KEY = 'beatduel_browse_swipe_hint_seen';
  let showSwipeHint = $state(false);

  $effect(() => {
    try {
      showSwipeHint = !localStorage.getItem(HINT_KEY);
    } catch {
      showSwipeHint = false;
    }
  });

  function dismissSwipeHint() {
    showSwipeHint = false;
    try {
      localStorage.setItem(HINT_KEY, '1');
    } catch {
      // no-op
    }
  }

  // Load duels when filters change (also triggers on mount)
  $effect(() => {
    // Track reactive deps so this re-runs when filters change
    filterGenre;
    filterMode;
    filterStatus;
    loadDuels();
  });

  async function loadDuels() {
    loading = true;
    try {
      const params = new URLSearchParams();
      if (filterGenre) params.set('genre_id', filterGenre);
      if (filterMode) params.set('mode', filterMode);
      if (filterStatus) params.set('status', filterStatus);
      const qs = params.toString();
      const data = await api.get<Duel[]>(`/duels${qs ? '?' + qs : ''}`);
      const all = Array.isArray(data) ? data : [];
      // Hide cards the user has dismissed via swipe-left this session.
      duels = filterDismissed(all);
    } catch (err) {
      error = (err as Error).message;
    } finally {
      loading = false;
    }
  }

  function handleDismiss(id: number) {
    dismissDuel(id);
    duels = duels.filter((d) => d.id !== id);
    dismissSwipeHint();
  }

  function clearFilters() {
    filterGenre = '';
    filterMode = '';
    filterStatus = '';
  }

  const hasFilters = $derived(!!filterGenre || !!filterMode || !!filterStatus);
</script>

<div>
  <!-- The former Home feed (personalized duels, season banner) now sits on top of the browse
       list — the standalone Home page was merged here so there's one duels surface. -->
  <HomeFeed />

  <h1>Duels</h1>
  <p class="browse-sub">Themed contests fought with catalog songs — claim and vote in the app.</p>

  {#if showSwipeHint}
    <div class="swipe-hint" role="status">
      <span class="swipe-hint__text">Swipe → to open, ← to dismiss</span>
      <button class="swipe-hint__close" onclick={dismissSwipeHint} aria-label="Dismiss tip">×</button>
    </div>
  {/if}

  <div class="mode-pills" role="group" aria-label="Filter by vote mode">
    <button class="mode-pill" class:mode-pill--active={filterMode === ''} onclick={() => (filterMode = '')}>
      All Modes
    </button>
    {#each VOTE_MODES as m (m)}
      <button class="mode-pill" class:mode-pill--active={filterMode === m} onclick={() => (filterMode = m)}>
        {modeLabel(m)}
      </button>
    {/each}
  </div>

  <div class="filters">
    <select bind:value={filterGenre} class="filter-select" aria-label="Filter by genre">
      <option value="">All Genres</option>
      <option value="1">Electronic</option>
      <option value="2">Pop</option>
      <option value="3">Rock</option>
      <option value="5">Hip Hop</option>
      <option value="6">Jazz</option>
      <option value="7">Country</option>
      <option value="8">Funk & Soul</option>
      <option value="9">Classical</option>
      <option value="10">Latin</option>
      <option value="11">Reggae</option>
      <option value="12">Blues</option>
    </select>

    <select bind:value={filterStatus} class="filter-select" aria-label="Filter by status">
      <option value="">All Status</option>
      <option value="open">Open</option>
      <option value="voting">Voting</option>
      <option value="ended">Ended</option>
    </select>

    {#if hasFilters}
      <button class="btn btn-sm btn-ghost" onclick={clearFilters}>Clear</button>
    {/if}
  </div>

  {#if loading}
    <LoadingSkeleton lines={5} />
  {:else if error}
    <p class="msg--error">{error}</p>
  {:else if duels.length === 0}
    <p class="empty">{hasFilters ? 'No duels match your filters.' : 'No duels found.'}</p>
  {:else}
    <!-- `grid-cards--browse` distinguishes the browse list (dismissable, mode-filtered
         cards) from the HomeFeed's own `.grid-cards` above it, so e2e can scope to
         the cards this page actually owns. -->
    <div class="grid-cards grid-cards--browse">
      {#each duels as duel (duel.id)}
        <DuelCard {duel} ondismiss={handleDismiss} />
      {/each}
    </div>
  {/if}
</div>

<style>
  h1 {
    margin-bottom: 0.375rem;
  }
  .browse-sub {
    margin: 0 0 0.75rem;
    color: var(--text-secondary);
    font-size: 0.9375rem;
  }
  .empty {
    color: var(--text-secondary);
  }

  .swipe-hint {
    display: none;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    margin-bottom: 1rem;
    padding: 0.625rem 0.875rem;
    background: var(--surface-2);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    font-size: 0.8125rem;
    color: var(--text-secondary);
  }
  .swipe-hint__text {
    flex: 1;
  }
  .swipe-hint__close {
    background: transparent;
    border: none;
    color: var(--text-muted);
    font-size: 1.25rem;
    line-height: 1;
    cursor: pointer;
    padding: 0 0.25rem;
  }
  .swipe-hint__close:hover {
    color: var(--text);
  }
  @media (max-width: 640px) {
    .swipe-hint {
      display: flex;
    }
  }

  .mode-pills {
    display: flex;
    gap: 0.5rem;
    margin-bottom: 0.75rem;
    flex-wrap: wrap;
  }
  .mode-pill {
    background: var(--surface);
    color: var(--text-muted);
    border: 1px solid var(--border);
    border-radius: 999px;
    padding: 0.35rem 0.85rem;
    font-size: 0.8125rem;
    font-weight: 600;
    cursor: pointer;
  }
  .mode-pill:hover {
    color: var(--text);
    border-color: var(--text-muted);
  }
  .mode-pill--active {
    color: var(--cyan);
    border-color: var(--cyan);
    background: rgba(0, 229, 255, 0.08);
  }

  .filters {
    display: flex;
    gap: 0.5rem;
    margin-bottom: 1.5rem;
    flex-wrap: wrap;
    align-items: center;
  }

  .filter-select {
    background: var(--surface);
    color: var(--text);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    padding: 0.5rem 0.75rem;
    font-size: 0.8125rem;
    cursor: pointer;
    appearance: auto;
  }
  .filter-select:focus {
    outline: 2px solid var(--cyan);
    outline-offset: 1px;
  }

  .btn-sm {
    font-size: 0.8125rem;
    padding: 0.375rem 0.75rem;
  }
  .btn-ghost {
    background: transparent;
    color: var(--text-muted);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    cursor: pointer;
  }
  .btn-ghost:hover {
    color: var(--text);
    border-color: var(--text-muted);
  }
</style>
